import React, { useEffect, useRef } from "react";
import { View, Text, Animated, Easing } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { Image } from "expo-image";
import Images from "@/assets/images";
import Svg, { Circle } from "react-native-svg";

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

interface CircularProgressProps {
  progress: Animated.Value;
  size?: number;
  strokeWidth?: number;
}

const CircularProgress = ({
  progress,
  size = 40,
  strokeWidth = 4,
}: CircularProgressProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const strokeDashoffset = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [circumference, 0],
  });

  return (
    <Svg width={size} height={size} style={styles.progressSvg}>
      {/* Background circle */}
      <Circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#E1EFE4"
        strokeWidth={strokeWidth}
        fill="none"
      />
      {/* Animated progress circle */}
      <AnimatedCircle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#0F9129"
        strokeWidth={strokeWidth}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        rotation="-90"
        origin={`${size / 2}, ${size / 2}`}
      />
    </Svg>
  );
};

interface OnboardSlide7Props {
  onComplete?: () => void;
}

const OnboardSlide7 = ({ onComplete }: OnboardSlide7Props) => {
  const progressValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // Animate progress from 0 to 1 over 2 seconds
    Animated.timing(progressValue, {
      toValue: 1,
      duration: 2000,
      easing: Easing.linear,
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished && onComplete) {
        onComplete();
      }
    });
  }, [progressValue, onComplete]);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <View style={styles.coinContainer}>
          <Image
            source={Images.coin3dGlassy}
            style={styles.coinImage}
            contentFit="contain"
          />
        </View>

        <Text style={styles.title}>
          Estimating money{"\n"}you haven't claimed
        </Text>
      </View>

      <View style={styles.loaderContainer}>
        <CircularProgress progress={progressValue} size={45} strokeWidth={7} />
      </View>
    </View>
  );
};

export default OnboardSlide7;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 32,
  },
  content: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
  },
  coinContainer: {
    width: 140,
    height: 147,
    alignItems: "center",
    justifyContent: "center",
  },
  coinImage: {
    width: 104,
    height: 92,
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 24,
    lineHeight: 32,
    color: "#161D14",
    textAlign: "center",
  },
  loaderContainer: {
    position: "absolute",
    bottom: -64,
    left: 0,
    right: 0,
    alignItems: "center",
  },
  progressSvg: {
    transform: [{ rotateZ: "0deg" }],
  },
});
